import {
  CrudFilters,
  HttpError,
  IResourceComponentsProps,
} from "@pankod/refine-core";
import {
  List,
  Table,
  TextField,
  useTable,
  getDefaultSortOrder,
  Space,
  EditButton,
  DeleteButton,
  TagField,
  ShowButton,
  Card,
  Form,
  Select,
  Button,
  Icons,
  useSelect,
  Row,
  Col,
} from "@pankod/refine-antd";
import { useEffect, useMemo, useState } from "react";
import { IContents } from "interfaces";
import { CacheNotificationCard } from "components/cards/cache-notification-card";

type ContentFiltersFormValues = {
  view?: string;
  key?: string;
};

const NO_FILTER_FORM_VALUES: ContentFiltersFormValues = {
  view: undefined,
  key: undefined,
};

type ContentFilterField = "view" | "key";
type NormalizedContentFilters = Record<ContentFilterField, string | null>;

const hasSelectedFilter = (value?: string): value is string =>
  typeof value === "string" && value.trim() !== "";

const normalizeSearchParams = (
  values: ContentFiltersFormValues,
): NormalizedContentFilters => ({
  view: hasSelectedFilter(values.view) ? values.view.trim() : null,
  key: hasSelectedFilter(values.key) ? values.key.trim() : null,
});

const filterSelectOptions = (
  input: string,
  option?: { label?: unknown },
): boolean => {
  return String(option?.label ?? "")
    .toLowerCase()
    .includes(input.toLowerCase());
};

const mapStringOptions = (items: unknown): { label: string; value: string }[] => {
  if (!Array.isArray(items)) {
    return [];
  }

  return items
    .filter((item): item is string => typeof item === "string")
    .map((item) => ({ label: item, value: item }));
};

const hasAnyActiveFilters = (values?: ContentFiltersFormValues): boolean =>
  hasSelectedFilter(values?.view) || hasSelectedFilter(values?.key);

export const ContentsList: React.FC<IResourceComponentsProps> = () => {
  const { tableProps, sorter, searchFormProps, setFilters } = useTable<
    IContents,
    HttpError,
    ContentFiltersFormValues
  >({
    defaultSetFilterBehavior: "replace",
    initialSorter: [
      {
        field: "key",
        order: "asc",
      },
    ],
    onSearch(data) {
      const params = normalizeSearchParams(data);
      const filterFields: readonly ContentFilterField[] = ["view", "key"];

      const filters: CrudFilters = filterFields
        .map((field) => ({
          field,
          operator: "eq" as const,
          value: params[field],
        }))
        .filter((filter) => filter.value !== null);

      return filters;
    },
  });
  const [hasActiveFilters, setHasActiveFilters] = useState(false);

  useEffect(() => {
    const form = searchFormProps.form;
    if (!form) return;

    setHasActiveFilters(
      hasAnyActiveFilters(
        form.getFieldsValue(["view", "key"]) as ContentFiltersFormValues,
      ),
    );
  }, [searchFormProps.form]);

  const { selectProps: viewSelectProps, queryResult: viewQueryResult } =
    useSelect({
      resource: "v1/cms/views",
      hasPagination: false,
    });

  const { selectProps: keySelectProps, queryResult: keyQueryResult } = useSelect({
    resource: "v1/cms/keys/",
    hasPagination: false,
  });

  const viewOptions = useMemo(
    () => [
      { label: "Todas", value: undefined },
      ...mapStringOptions(viewQueryResult.data?.data),
    ],
    [viewQueryResult.data?.data],
  );

  const keyOptions = useMemo(
    () => [
      { label: "Todas", value: undefined },
      ...mapStringOptions(keyQueryResult.data?.data),
    ],
    [keyQueryResult.data?.data],
  );

  const handleClearFilters = () => {
    const form = searchFormProps.form;
    if (!form) return;

    form.setFieldsValue(NO_FILTER_FORM_VALUES);
    setHasActiveFilters(false);
    setFilters([], "replace");
  };

  const handleFiltersValueChange = (
    _: unknown,
    allValues: ContentFiltersFormValues,
  ) => {
    setHasActiveFilters(hasAnyActiveFilters(allValues));
  };

  return (
    <>
      <List>
        <CacheNotificationCard />
        <Card title="Filtrar">
          <Form
            {...searchFormProps}
            layout="vertical"
            style={{ padding: 16 }}
            onValuesChange={handleFiltersValueChange}
          >
            <Row gutter={16}>
              <Col xs={24} sm={12}>
                <Form.Item name="view" label="Filtrar Pagina:">
                  <Select
                    {...viewSelectProps}
                    options={viewOptions}
                    placeholder="Todas"
                    showSearch
                    optionFilterProp="label"
                    filterOption={filterSelectOptions}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} sm={12}>
                <Form.Item name="key" label="Filtrar Chave:">
                  <Select
                    {...keySelectProps}
                    options={keyOptions}
                    placeholder="Todas"
                    showSearch
                    optionFilterProp="label"
                    filterOption={filterSelectOptions}
                  />
                </Form.Item>
              </Col>

              <Col xs={24} style={{ textAlign: "right", marginTop: 8 }}>
                <Space>
                  <Button
                    type="default"
                    icon={<Icons.ClearOutlined />}
                    onClick={handleClearFilters}
                    disabled={!hasActiveFilters}
                  >
                    Limpar filtros
                  </Button>
                  <Button
                    type="primary"
                    icon={<Icons.SearchOutlined />}
                    htmlType="submit"
                  >
                    Buscar
                  </Button>
                </Space>
              </Col>
            </Row>
          </Form>
        </Card>
        <Table {...tableProps} rowKey="_id" style={{ tableLayout: "fixed", width: "100%" }}>
          <Table.Column
            dataIndex="view"
            key="view"
            title="Pagina"
            render={(value) => <TextField value={value} />}
            defaultSortOrder={getDefaultSortOrder("view", sorter)}
            sorter
          />
          <Table.Column
            dataIndex="key"
            key="key"
            title="Chave"
            render={(value) => <TextField value={value} />}
            defaultSortOrder={getDefaultSortOrder("key", sorter)}
            sorter
          />
          <Table.Column
            dataIndex="value"
            key="value"
            title="Valor"
            render={(value) => (
              <TagField
                value={value}
                style={{ wordBreak: "break-all", textWrap: "wrap", maxWidth: "100%" }}
              />
            )}
            defaultSortOrder={getDefaultSortOrder("value", sorter)}
            sorter
          />

          <Table.Column<IContents>
            title="Ações"
            dataIndex="actions"
            render={(_, record) => (
              <Space>
                <EditButton hideText size="small" recordItemId={record._id} />
                <ShowButton hideText size="small" recordItemId={record._id} />
                <DeleteButton hideText size="small" recordItemId={record._id} />
              </Space>
            )}
          />
        </Table>
      </List>
    </>
  );
};
